import { ExploreCompiler } from '../compiler/exploreCompiler';
import { SupportedDbtAdapter } from '../types/dbt';
import { ExploreType } from '../types/explore';
import { DimensionType, FieldType, friendlyName, } from '../types/field';
import { WarehouseTypes } from '../types/projects';
import { getDefaultTimeFrames, timeFrameConfigs, WeekDay } from './timeFrames';
import { defaultNullSafeEqualSql, quoteFieldReference } from './warehouse';
const isIntervalColumnType = (type) => type === DimensionType.DATE || type === DimensionType.TIMESTAMP;
/**
 * Build one dimension per column, plus the default time-interval dimensions
 * for date/timestamp columns. Shared by virtual views and external source
 * explores — both have no meta layer to override intervals.
 */
export const buildDimensionsFromColumns = (args) => {
    const { tableName, tableLabel, columns, warehouseSqlBuilder } = args;
    const fieldQuoteChar = warehouseSqlBuilder.getFieldQuoteChar();
    const adapterType = warehouseSqlBuilder.getAdapterType();
    const startOfWeek = warehouseSqlBuilder.getStartOfWeek();
    const columnReferences = new Set(columns.map(({ reference }) => reference));
    return columns.reduce((acc, column) => {
        const type = column.type ?? DimensionType.STRING;
        const columnLabel = friendlyName(column.reference);
        const columnReference = quoteFieldReference(column.reference, fieldQuoteChar, adapterType);
        const columnSql = args.qualifyColumnReferences
            ? `\${TABLE}.${columnReference}`
            : columnReference;
        const isIntervalBase = isIntervalColumnType(type);
        acc[column.reference] = {
            name: column.reference,
            label: columnLabel,
            type,
            table: tableName,
            fieldType: FieldType.DIMENSION,
            sql: columnSql,
            tableLabel,
            hidden: false,
            isIntervalBase,
        };
        if (isIntervalBase) {
            getDefaultTimeFrames(type).forEach((timeInterval) => {
                const intervalName = `${column.reference}_${timeInterval.toLowerCase()}`;
                // A real column of the same name always wins
                if (columnReferences.has(intervalName)) {
                    return;
                }
                const intervalConfig = timeFrameConfigs[timeInterval];
                acc[intervalName] = {
                    name: intervalName,
                    label: `${columnLabel} ${intervalConfig
                        .getLabel()
                        .toLowerCase()}`,
                    type: intervalConfig.getDimensionType(type),
                    table: tableName,
                    fieldType: FieldType.DIMENSION,
                    sql: intervalConfig.getSql(adapterType, timeInterval, columnSql, type, startOfWeek),
                    tableLabel,
                    hidden: false,
                    timeInterval,
                    timeIntervalBaseDimensionName: column.reference,
                    timeIntervalBaseDimensionType: type,
                    groups: [columnLabel],
                    isIntervalBase: false,
                };
            });
        }
        return acc;
    }, {});
};
export const createVirtualView = (virtualViewName, sql, columns, warehouseClient, label, parameterValues) => {
    const exploreCompiler = new ExploreCompiler(warehouseClient);
    const tableLabel = friendlyName(virtualViewName);
    const dimensions = buildDimensionsFromColumns({
        tableName: virtualViewName,
        tableLabel,
        columns,
        warehouseSqlBuilder: warehouseClient,
    });
    const compiledTable = {
        name: virtualViewName,
        label: label || friendlyName(virtualViewName),
        sqlTable: `(${sql})`, // Wrap the sql in a subquery to avoid issues with reserved words
        dimensions,
        metrics: {},
        lineageGraph: { nodes: [], edges: [] },
        database: warehouseClient.credentials.type,
        schema: '', // TODO: what should this be?
    };
    const explore = exploreCompiler.compileExplore({
        name: virtualViewName,
        label: label || friendlyName(virtualViewName),
        tags: [],
        baseTable: virtualViewName,
        joinedTables: [],
        tables: { [virtualViewName]: compiledTable },
        targetDatabase: warehouseClient.getAdapterType(),
        meta: {},
    });
    const hasSavedParameters = parameterValues && Object.keys(parameterValues).length > 0;
    const virtualView = {
        ...explore,
        type: ExploreType.VIRTUAL,
        ...(hasSavedParameters
            ? { savedParameterValues: parameterValues }
            : {}),
    };
    return virtualView;
};
export const createTemporaryVirtualView = (virtualViewName, sql, columns) => {
    // Create a fake warehouseClient for compilation purposes
    const fakeWarehouseClient = {
        getSessionTimezone: async () => null,
        credentials: {
            type: WarehouseTypes.BIGQUERY,
            project: '',
            dataset: '',
            timeoutSeconds: 0,
            priority: 'interactive',
            keyfileContents: {},
            retries: 3,
            location: '',
            maximumBytesBilled: 0,
        },
        getCatalog: async () => ({}),
        streamQuery: async () => { },
        executeAsyncQuery: async () => ({
            queryId: null,
            queryMetadata: null,
            totalRows: 0,
            durationMs: 0,
            phaseTimings: {},
        }),
        runQuery: async () => ({ fields: {}, rows: [] }),
        test: async () => { },
        getStartOfWeek: () => WeekDay.MONDAY,
        getAdapterType: () => SupportedDbtAdapter.BIGQUERY,
        supportsCteMaterialization: () => true,
        getStringQuoteChar: () => "'",
        getEscapeStringQuoteChar: () => "''",
        getFieldQuoteChar: () => '"',
        getUnnestSql: () => null,
        getFloatingType: () => 'FLOAT',
        getNullSafeEqualSql: defaultNullSafeEqualSql,
        getNullSafeEqualJoinSql: defaultNullSafeEqualSql,
        getMetricSql: () => '',
        concatString: (...args) => args.join(''),
        getAllTables: async () => [],
        getFields: async () => ({}),
        parseWarehouseCatalog: () => ({}),
        parseError: (error) => error,
        escapeString: (value) => value,
        castToTimestamp: (date) => `CAST('${date.toISOString()}' AS TIMESTAMP)`,
        castToDate: (date) => `CAST('${date.toISOString().slice(0, 10)}' AS DATE)`,
        castToNaiveTimestamp: (date) => `CAST('${date.toISOString()}' AS TIMESTAMP)`,
        getIntervalSql: (value, unit) => `INTERVAL '${value} ${unit}'`,
        getTimestampDiffSeconds: (startTimestampSql, endTimestampSql) => `EXTRACT(EPOCH FROM (${endTimestampSql} - ${startTimestampSql}))`,
        getMedianSql: (valueSql) => `PERCENTILE_CONT(0.5) WITHIN GROUP (ORDER BY ${valueSql})`,
        buildArray: (elements) => `ARRAY[${elements.join(', ')}]`,
        buildArrayAgg: (expression, orderBy) => orderBy
            ? `ARRAY_AGG(${expression} ORDER BY ${orderBy})`
            : `ARRAY_AGG(${expression})`,
    };
    return createVirtualView(virtualViewName, sql, columns, fakeWarehouseClient);
};
//# sourceMappingURL=virtualView.js.map